//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import * as twitterAnalyticsApi from '@apis/twitter-analytics/twitterAnalyticsApi';
import {
  TWITTER_API_DATE_FORMAT,
  ITwitterAudienceDailyStats,
  ITwitterAudienceSummaryStats,
} from '@apis/twitter-analytics/twitterAnalyticsApiModel';
import {
  getAliasedDateRange,
  IAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { createThunk } from '@redux/createThunk';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { SliceNames } from '@redux/SliceNames';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ITwitterAudiencePageState,
  ITwitterDailyAudienceMetric,
} from '@twitterV2/redux/audience/twitterAudienceModel';
import { fillInMissingAudienceDates } from '@twitterV2/twitterUtils';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import { crl8 } from 'app/curalate.globals';

const initialState: ITwitterAudiencePageState = {
  dateRange: getAliasedDateRange(
    'last7Days',
    crl8.session?.timeZone.id ?? TimeZoneId.Utc,
  ),
  dailyStats: undefined,
  earliestMetricsDate: undefined,
  summaryStats: undefined,
  flags: {
    isFetchingDailyStats: false,
    isFetchingSummaryStats: false,
  },
  errors: {
    fetchDailyStatsError: undefined,
    fetchSummaryStatsError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.TwitterAudience,
  initialState,
  reducers: {
    updateDateRange(state, action: PayloadAction<IAliasedDateRange>) {
      state.dateRange = action.payload;
    },
    requestDailyStats(state) {
      state.flags.isFetchingDailyStats = true;
      state.errors.fetchDailyStatsError = undefined;
    },
    receiveDailyStatsSucceeded(
      state,
      action: PayloadAction<{
        stats: ITwitterDailyAudienceMetric[];
        earliestMetricsDate: DateString;
      }>,
    ) {
      state.flags.isFetchingDailyStats = false;
      state.dailyStats = [...action.payload.stats];
      state.earliestMetricsDate = action.payload.earliestMetricsDate;
    },
    receiveDailyStatsFailed(state, action: PayloadAction<string | undefined>) {
      state.flags.isFetchingDailyStats = false;
      state.errors.fetchDailyStatsError = action.payload;
    },
    requestSummaryStats(state) {
      state.flags.isFetchingSummaryStats = true;
      state.errors.fetchSummaryStatsError = undefined;
    },
    receiveSummaryStatsSucceeded(
      state,
      action: PayloadAction<ITwitterAudienceSummaryStats>,
    ) {
      state.flags.isFetchingSummaryStats = false;
      state.summaryStats = { ...action.payload };
    },
    receiveSummaryStatsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingSummaryStats = false;
      state.errors.fetchSummaryStatsError = action.payload;
    },
  },
});

export const changeDateRangeAndRefreshContent = createThunk(
  (dateRange: IAliasedDateRange) => (dispatch) => {
    dispatch(actions.updateDateRange(dateRange));

    void dispatch(fetchAudienceSummaryStats(dateRange));
    void dispatch(fetchAudienceDailyStats());
  },
);

export const fetchAudienceSummaryStats = createThunk(
  (dateRange: IAliasedDateRange) => async (dispatch, getState) => {
    const twitterUserId = getState().twitter.user.user?.id;
    const twitterUsername = getState().twitter.user.user?.screenName;
    const userTimeZone = getUserTimeZone(getState());

    if (!twitterUserId || !twitterUsername) {
      return;
    }

    dispatch(actions.requestSummaryStats());

    try {
      const statsData = await twitterAnalyticsApi.getAudienceSummaryMetrics({
        twitterUserId,
        twitterUsername,
        startTime: DateString.toDate(dateRange.startDate, userTimeZone),
        endTime: DateString.toDate(dateRange.endDate, userTimeZone),
      });

      dispatch(actions.receiveSummaryStatsSucceeded(statsData.data));
    } catch (e) {
      dispatch(actions.receiveSummaryStatsFailed(getRequestErrorMessage(e)));
    }
  },
);

export const fetchAudienceDailyStats = createThunk(
  () => async (dispatch, getState) => {
    const twitterUserId = getState().twitter.user.user?.id;
    const { dateRange } = getState().twitter.audience;
    const userTimeZone = getUserTimeZone(getState());

    if (!twitterUserId) {
      return;
    }

    dispatch(actions.requestDailyStats());

    try {
      const statsData = await twitterAnalyticsApi.getAudienceDailyMetrics({
        twitterUserId,
        startTime: DateString.toDate(dateRange.startDate, userTimeZone),
        endTime: DateString.toDate(dateRange.endDate, userTimeZone),
      });

      const formattedStatsData = fillInMissingAudienceDates(
        statsData.data.dailyMetrics,
        dateRange.startDate,
        dateRange.endDate,
      );

      const earliestMetricsDate = DateString.fromString(
        statsData.data.earliestMetricsDate,
        TWITTER_API_DATE_FORMAT,
      );

      dispatch(
        actions.receiveDailyStatsSucceeded({
          stats: formattedStatsData,
          earliestMetricsDate: earliestMetricsDate,
        }),
      );
    } catch (e) {
      dispatch(actions.receiveDailyStatsFailed(getRequestErrorMessage(e)));
    }
  },
);

export default reducer;
